import React, { useEffect, useState } from 'react'
import { Image, Platform } from 'react-native'

import { Icon, Pressable, Stack, XStack } from '@zet/design-system'
import { getDescryptJwtToken } from '@zet/integration-flow/hooks/useGetDescrptJwt'
import { B14, M12 } from '@zet/integration-flow/components/typography'
import { isWebView } from '@zet/integration-flow/utils'
import { sendToApp } from '@zet/integration-flow/utils/webViewBridge'

import jioPlusAvatarBase64 from '../../assets/images/jioPlusAvatarBase64'

const JIOFINANCE_PLUS_DEEPLINK = 'jfs_valueBack_pdp?vendorJourney=true'

const refreshJwtAndGetSubscribed = (): Promise<boolean | null> => {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(
      () => reject(new Error('refreshJwtAuthToken timed out')),
      10000,
    )

    ;(window as any).sendRefreshJwtAuthToken = async (data: any) => {
      clearTimeout(timeout)
      try {
        const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent)
        let parsed = data
        if (!isIOS) {
          parsed = JSON.parse(data)
        }
        const jwt = isIOS ? parsed : parsed?.data?.jwtToken
        if (!jwt) throw new Error('No JWT in refresh response')

        const decoded = await getDescryptJwtToken(jwt)
        const updatedSubscribed = (decoded?.actionData as any)?.subscribed
        resolve(updatedSubscribed !== undefined && updatedSubscribed !== null
          ? Boolean(updatedSubscribed)
          : null)
      } catch (err) {
        reject(err)
      }
    }

    sendToApp({ type: 'refreshJwtAuthToken', microAppID: 'ZETAPP' })
  })
}

const handleCashbackBannerPress = () => {
  sessionStorage.setItem('jfsBannerClicked', 'true')

  const payloadObj = {
    type: 'VENDOR_PAGE_CLICK',
    data: { deeplink: JIOFINANCE_PLUS_DEEPLINK },
  }
  const payload = JSON.stringify(payloadObj)
  const encodedPayload = window.btoa(payload)

  console.log('[JioCashback] Banner pressed, payload:', payload)
  console.log('[JioCashback] Bridge availability:', {
    JFS: !!(window as any)?.JFS,
    JFSJavaInterface: !!(window as any)?.JFSJavaInterface,
    ReactNativeWebView: !!window?.ReactNativeWebView,
    android: !!(window as any)?.android?.__externalCall,
    externalCall: !!(window as any)?.__externalCall,
    webkit: !!(window as any)?.webkit?.messageHandlers?.callback,
  })

  // Section 4.1.3 — primary example for VENDOR_PAGE_CLICK
  ;(window as any)?.JFS?.postMessage(payload)
  // Section 3.1 — Android native WebView
  ;(window as any)?.JFSJavaInterface?.receiveString(payload)
  // Section 3.1 — React Native WebView (Android + iOS)
  window?.ReactNativeWebView?.postMessage(payload)
  // Section 3.2 — iOS WKWebView (called unconditionally so else-if chain cannot block it)
  ;(window as any)?.webkit?.messageHandlers?.callback?.postMessage(encodedPayload)
  // Section 3.2 — Android generic bridge (base64-encoded)
  ;(window as any)?.android?.__externalCall?.(encodedPayload)
  ;(window as any)?.__externalCall?.(encodedPayload)
}

const JioCashbackBanner = () => {
  const [subscribed, setSubscribed] = useState<boolean | null>(() => {
    if (Platform.OS !== 'web' || !isWebView()) return null
    const val = sessionStorage.getItem('jfsSubscribed')
    return val === null ? null : val === 'true'
  })

  useEffect(() => {
    if (Platform.OS !== 'web' || !isWebView()) return

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return
      if (sessionStorage.getItem('jfsBannerClicked') !== 'true') return

      sessionStorage.removeItem('jfsBannerClicked')

      try {
        const updatedSubscribed = await refreshJwtAndGetSubscribed()
        if (updatedSubscribed !== null) {
          sessionStorage.setItem('jfsSubscribed', String(updatedSubscribed))
          setSubscribed(updatedSubscribed)
        }
      } catch (err) {
        console.error('[JioCashback] Token refresh after return failed:', err)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  if (Platform.OS !== 'web' || !isWebView() || subscribed === null) return null

  const title = subscribed
    ? "You'll earn up to ₹1,000 cashback"
    : 'Assured cashback up to ₹1,000'
  const subtitle = subscribed
    ? 'Enjoy your exclusive benefits'
    : 'Upgrade to JioFinance+ for free'

  return (
    <Pressable
      onPress={subscribed ? undefined : handleCashbackBannerPress}
      className="mx-3 my-2 border border-[#E4E4E4] rounded-xl px-3 py-[10px]"
    >
      <XStack className="items-center gap-3">
        <Image
          source={{ uri: jioPlusAvatarBase64 }}
          style={{ width: 36, height: 36 }}
        />
        <Stack className="flex-1">
          <B14 className="text-[#1C1D1F]">{title}</B14>
          <M12 className="text-[#6B6B6B] mt-[2px]">{subtitle}</M12>
        </Stack>
        {!subscribed && <Icon type="ArrowRight" color="#1C1D1F" size={20} />}
      </XStack>
    </Pressable>
  )
}

export default JioCashbackBanner
